import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { CloudUpload, Loader2, CheckCircle2, X, FileText } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CHUNK = 4 * 1024 * 1024;

const randomId = () => Array.from(crypto.getRandomValues(new Uint8Array(16))).map((b) => b.toString(16).padStart(2, "0")).join("");

export default function DropPage() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [name, setName] = useState("");
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [doneCount, setDoneCount] = useState(0);

  useEffect(() => {
    axios.get(`${API}/drop/${token}`)
      .then(({ data }) => (data.valid ? setInfo(data) : setError(data.reason || "Link ungültig")))
      .catch((e) => setError(e.response?.status === 404 ? "Dieser Link existiert nicht." : "Link konnte nicht geladen werden."));
  }, [token]);

  const addFiles = (e) => {
    const list = Array.from(e.target.files || []).map((f) => ({ file: f, status: "wartend", progress: 0 }));
    setFiles((prev) => [...prev, ...list]);
    e.target.value = "";
  };

  const uploadOne = async (entry, idx) => {
    const uploadId = randomId();
    const total = Math.max(1, Math.ceil(entry.file.size / CHUNK));
    for (let i = 0; i < total; i++) {
      const fd = new FormData();
      fd.append("upload_id", uploadId);
      fd.append("index", String(i));
      fd.append("chunk", entry.file.slice(i * CHUNK, (i + 1) * CHUNK));
      await axios.post(`${API}/drop/${token}/chunk`, fd);
      setFiles((prev) => prev.map((f, j) => (j === idx ? { ...f, progress: Math.round(((i + 1) / total) * 90) } : f)));
    }
    const fd = new FormData();
    fd.append("upload_id", uploadId);
    fd.append("filename", entry.file.name);
    fd.append("total", String(total));
    fd.append("uploader_name", name.trim());
    await axios.post(`${API}/drop/${token}/complete`, fd);
    setFiles((prev) => prev.map((f, j) => (j === idx ? { ...f, status: "fertig", progress: 100 } : f)));
  };

  const startUpload = async () => {
    if (!name.trim()) { setError(""); alert("Bitte gib zuerst deinen Namen an."); return; }
    setBusy(true);
    let done = 0;
    for (let i = 0; i < files.length; i++) {
      if (files[i].status === "fertig") { done++; continue; }
      setFiles((prev) => prev.map((f, j) => (j === i ? { ...f, status: "lädt" } : f)));
      try {
        await uploadOne(files[i], i);
        done++;
      } catch (e) {
        setFiles((prev) => prev.map((f, j) => (j === i ? { ...f, status: "fehler", error: e.response?.data?.detail || "Upload fehlgeschlagen" } : f)));
      }
    }
    setDoneCount(done);
    setBusy(false);
  };

  const allDone = files.length > 0 && files.every((f) => f.status === "fertig");

  if (error) {
    return (
      <div className="min-h-screen bg-neutral-950 flex items-center justify-center p-6">
        <div className="text-center max-w-sm">
          <X className="h-10 w-10 text-rose-500 mx-auto mb-3" />
          <p className="text-white font-bold text-lg" data-testid="drop-error">{error}</p>
          <p className="text-neutral-400 text-sm mt-2">Bitte wende dich an dein Team für einen neuen Link.</p>
        </div>
      </div>
    );
  }
  if (!info) return <div className="min-h-screen bg-neutral-950 flex items-center justify-center"><Loader2 className="h-8 w-8 text-white animate-spin" /></div>;

  return (
    <div className="min-h-screen bg-neutral-950 flex items-center justify-center p-4 sm:p-6">
      <div className="w-full max-w-lg bg-white rounded-3xl shadow-2xl p-6 sm:p-8" data-testid="drop-page">
        <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-orange-500">Datei-Upload</p>
        <h1 className="text-2xl font-bold text-neutral-900 mt-1" data-testid="drop-title">{info.name}</h1>
        <p className="text-sm text-neutral-400 mt-1">Lade hier deine Dateien hoch — sie landen direkt beim Team.</p>

        {allDone ? (
          <div className="mt-8 text-center" data-testid="drop-success">
            <CheckCircle2 className="h-12 w-12 text-green-500 mx-auto mb-3" />
            <p className="font-bold text-neutral-900">{doneCount} Datei{doneCount === 1 ? "" : "en"} erfolgreich hochgeladen!</p>
            <p className="text-sm text-neutral-400 mt-1">Das Team wurde benachrichtigt. Du kannst das Fenster schließen.</p>
            <button onClick={() => { setFiles([]); setDoneCount(0); }} className="mt-4 text-sm font-semibold text-orange-600">Weitere Dateien hochladen</button>
          </div>
        ) : (
          <div className="mt-6 space-y-4">
            <div>
              <label className="text-xs font-semibold text-neutral-600">Dein Name *</label>
              <input data-testid="drop-name" value={name} onChange={(e) => setName(e.target.value)} disabled={busy}
                placeholder="z. B. Max Mustermann"
                className="mt-1 w-full h-11 px-3 rounded-xl border border-neutral-200 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/30" />
            </div>
            <label className="block border-2 border-dashed border-neutral-300 rounded-2xl p-8 text-center cursor-pointer hover:border-orange-400 transition" data-testid="drop-zone">
              <input type="file" multiple hidden onChange={addFiles} disabled={busy} data-testid="drop-file-input" />
              <CloudUpload className="h-8 w-8 text-orange-500 mx-auto mb-2" />
              <p className="text-sm font-semibold text-neutral-700">Dateien auswählen</p>
              <p className="text-xs text-neutral-400 mt-1">Fotos, Videos, Dokumente — bis 500 MB pro Datei</p>
            </label>
            {files.length > 0 && (
              <div className="space-y-2">
                {files.map((f, i) => (
                  <div key={i} className="flex items-center gap-2.5 bg-neutral-50 border border-neutral-200 rounded-xl px-3 py-2.5">
                    <FileText className="h-4 w-4 text-neutral-400 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-neutral-700 truncate">{f.file.name}</p>
                      {f.status === "lädt" && <div className="h-1 bg-neutral-200 rounded-full mt-1"><div className="h-1 bg-orange-500 rounded-full transition-all" style={{ width: `${f.progress}%` }} /></div>}
                      {f.status === "fehler" && <p className="text-[11px] text-rose-500">{f.error}</p>}
                    </div>
                    {f.status === "fertig" && <CheckCircle2 className="h-4 w-4 text-green-500 shrink-0" />}
                    {f.status === "lädt" && <Loader2 className="h-4 w-4 text-orange-500 animate-spin shrink-0" />}
                    {f.status === "wartend" && !busy && (
                      <button onClick={() => setFiles((prev) => prev.filter((_, j) => j !== i))} className="text-neutral-300 hover:text-rose-500"><X className="h-4 w-4" /></button>
                    )}
                  </div>
                ))}
              </div>
            )}
            <button data-testid="drop-upload-btn" onClick={startUpload} disabled={busy || files.length === 0}
              className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl bg-orange-500 text-white font-bold text-sm hover:bg-orange-600 disabled:opacity-50 transition">
              {busy ? <><Loader2 className="h-4 w-4 animate-spin" /> Lädt hoch…</> : `${files.length || ""} Datei${files.length === 1 ? "" : "en"} hochladen`}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
